import { useState } from 'react';
import type { RefObject } from 'react';
import styled, { keyframes } from 'styled-components';

import { IconFilePlus } from '../../common/icons/cdl/FilePlus';
import { Box } from '../../common/ui/Box';

import {
    buildActivityTimelineItems,
    getMessageSenderKey,
    getTimelineItemSpacingBefore,
    getTimelineSpacingItem,
} from './activityTimelineItems';
import type { ActivityTimelineItem } from './activityTimelineItems';
import { CollapsedSystemTimelineItem } from './CollapsedSystemTimelineItem';
import { SystemTimelineItem } from './SystemTimelineItem';
import type { OfferChatMessage } from './types';
import { UserTimelineItem } from './UserTimelineItem';

const revealTimelineItem = keyframes`
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;

const RevealedTimelineItem = styled(Box)<{ $index: number }>`
    opacity: 0;
    animation-name: ${revealTimelineItem};
    animation-duration: 75ms;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    animation-delay: ${({ $index }) => $index * 10}ms;
`;

interface ActivityTimelineMessagesProps {
    messages: OfferChatMessage[];
    messageBoxRef?: RefObject<HTMLDivElement | null>;
}

const isSameMinute = (firstDate: string, secondDate: string): boolean => {
    const firstTimestamp = new Date(firstDate).getTime();
    const secondTimestamp = new Date(secondDate).getTime();

    return Math.floor(firstTimestamp / 60000) === Math.floor(secondTimestamp / 60000);
};

interface ActivityTimelineRenderItem {
    item: ActivityTimelineItem;
    previousHasTimelineRail: boolean;
    nextHasTimelineRail: boolean;
    spacingBefore: number;
    hideHeader: boolean;
}

const hasTimelineRail = (item: ActivityTimelineItem | null): boolean => {
    if (!item) {
        return false;
    }

    return item.type === 'collapsedSystemMessages' || Boolean(item.message.systemMessage);
};

const buildActivityTimelineRenderItems = (items: ActivityTimelineItem[]): ActivityTimelineRenderItem[] => {
    return items.map((item, index) => {
        const previousItem = index > 0 ? items[index - 1] : null;
        const nextItem = index === items.length - 1 ? null : items[index + 1];
        const previousHasTimelineRail = hasTimelineRail(previousItem);
        const nextHasTimelineRail = hasTimelineRail(nextItem);
        const spacingBefore = getTimelineItemSpacingBefore(
            previousItem ? getTimelineSpacingItem(previousItem) : null,
            getTimelineSpacingItem(item)
        );
        const hideHeader =
            item.type === 'message' &&
            !item.message.systemMessage &&
            previousItem?.type === 'message' &&
            !previousItem.message.systemMessage &&
            isSameMinute(previousItem.message.dateCreated, item.message.dateCreated) &&
            getMessageSenderKey(previousItem.message) === getMessageSenderKey(item.message);

        return {
            item,
            previousHasTimelineRail,
            nextHasTimelineRail,
            spacingBefore,
            hideHeader,
        };
    });
};

export const ActivityTimelineMessages = ({ messages, messageBoxRef }: ActivityTimelineMessagesProps) => {
    const [shownSystemMessageGroups, setShownSystemMessageGroups] = useState<Record<string, boolean>>({});
    const activityTimelineItems = buildActivityTimelineItems(messages);
    const renderItems = buildActivityTimelineRenderItems(activityTimelineItems);

    return (
        <Box ref={messageBoxRef}>
            {renderItems.map(({ item, previousHasTimelineRail, nextHasTimelineRail, spacingBefore, hideHeader }) => {
                if (item.type === 'collapsedSystemMessages') {
                    const shown = Boolean(shownSystemMessageGroups[item.id]);

                    return (
                        <Box key={item.id} marginTop={spacingBefore}>
                            {shown ? null : (
                                <CollapsedSystemTimelineItem
                                    messages={item.messages}
                                    lineBefore={previousHasTimelineRail}
                                    lineAfter={nextHasTimelineRail}
                                    onShow={() =>
                                        setShownSystemMessageGroups((groups) => ({
                                            ...groups,
                                            [item.id]: true,
                                        }))
                                    }
                                />
                            )}
                            {shown
                                ? item.messages.map((message, messageIndex) => (
                                      <RevealedTimelineItem key={message.id} $index={messageIndex}>
                                          <SystemTimelineItem
                                              message={message}
                                              lineBefore={
                                                  messageIndex > 0 || (messageIndex === 0 && previousHasTimelineRail)
                                              }
                                              lineAfter={
                                                  messageIndex < item.messages.length - 1 ||
                                                  (messageIndex === item.messages.length - 1 && nextHasTimelineRail)
                                              }
                                          />
                                      </RevealedTimelineItem>
                                  ))
                                : null}
                        </Box>
                    );
                }

                const { message } = item;

                if (message.systemMessage) {
                    return (
                        <Box key={message.id} marginTop={spacingBefore}>
                            <SystemTimelineItem
                                message={message}
                                lineBefore={previousHasTimelineRail}
                                lineAfter={nextHasTimelineRail}
                                Icon={item === activityTimelineItems[0] ? IconFilePlus : undefined}
                            />
                        </Box>
                    );
                }

                return (
                    <Box key={message.id} marginTop={spacingBefore}>
                        <UserTimelineItem message={message} hideHeader={hideHeader} />
                    </Box>
                );
            })}
        </Box>
    );
};
